import {
    AirspaceFeatureProperties,
    AirspaceFeatureStatus,
    AirspaceFeatureType,
    AirspaceNotamType,
    OpenAirAltitude,
} from "@nova/constants"
import { Status } from "@nova/ds-component-status"
import * as Icons from "@nova/component-icons"
import { useRestriction } from "features/map/layers/airspace/layers/restriction/restrictionCache"
import { useNotam } from "features/map/layers/airspace/layers/notam/notamCache"
import { useTFR } from "features/map/layers/airspace/layers/tfr/tfrCache"
import { ExpandableContent } from "@nova/component-expandable-content"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { TabHeader } from "themes/default-tailwind/buttons/TabHeader"
import classNames from "classnames"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectRawTextExpanded,
    selectRawTextTab,
    setRawTextExpanded,
    setRawTextTab,
} from "features/overview/slice"

export const AirspaceFeaturePanel = ({
    id,
    source,
}: {
    id: string
    source: AirspaceFeatureType
}) => {
    const restriction = useRestriction(source === "restriction" ? id : "")
    const notam = useNotam(source === "notam" ? id : "")
    const tfr = useTFR(source === "tfr" ? id : "")
    const airspace = source === "restriction" ? restriction : source === "notam" ? notam : tfr
    if (!airspace) return null
    return <AirspaceFeaturePanelContent properties={airspace.properties} />
}

const AirspaceFeaturePanelContent = ({ properties }: { properties: AirspaceFeatureProperties }) => {
    const {
        status,
        title,
        featureType,
        notamType,
        location,
        id,
        startsAt,
        endsAt,
        altitudeLower,
        altitudeUpper,
        condition,
        reason,
        affects,
        usingAgencyName,
        usingAgencyPhone,
        controllingAgencyName,
        controllingAgencyPhone,
        rawText,
        rawIcaoFormat,
        rawLocalFormat,
    } = properties
    const hasTimeRange = startsAt !== undefined || endsAt !== undefined
    const hasAltitudeRange = altitudeLower !== undefined || altitudeUpper !== undefined
    const hasRawText = featureType !== "restriction"
    return (
        <div className="pointer-events-auto w-[300px] rounded-lg bg-black-800 flex flex-col p-5 gap-3 backdrop-blur-sm">
            <AirspaceFeaturePanelTitle
                id={id}
                status={status}
                featureType={featureType}
                notamType={notamType}
                title={title}
                location={location}
            />
            {hasTimeRange && (
                <>
                    <div className="w-full h-px bg-white-200" />
                    <AirspaceFeaturePanelTimeRange start={startsAt} end={endsAt} />
                </>
            )}
            {hasAltitudeRange && (
                <>
                    <div className="w-full h-px bg-white-200" />
                    <AirspaceFeaturePanelAltitudes floor={altitudeLower} ceiling={altitudeUpper} />
                </>
            )}
            {condition && (
                <>
                    <div className="w-full h-px bg-white-200" />
                    <AirspaceFeaturePanelSimpleText label="Condition" value={condition} />
                </>
            )}
            {reason && (
                <>
                    <div className="w-full h-px bg-white-200" />
                    <AirspaceFeaturePanelSimpleText label="Reason" value={reason} />
                </>
            )}
            {affects && (
                <>
                    <div className="w-full h-px bg-white-200" />
                    <AirspaceFeaturePanelSimpleText label="Affects" value={affects} />
                </>
            )}
            {usingAgencyName && (
                <>
                    <div className="w-full h-px bg-white-200" />
                    <AirspaceFeaturePanelSimpleText
                        label="Using Agency"
                        value={formatAgencyContact(usingAgencyName, usingAgencyPhone)}
                    />
                </>
            )}
            {controllingAgencyName && (
                <>
                    <div className="w-full h-px bg-white-200" />
                    <AirspaceFeaturePanelSimpleText
                        label="Controlling Agency"
                        value={formatAgencyContact(controllingAgencyName, controllingAgencyPhone)}
                    />
                </>
            )}
            {hasRawText && (
                <>
                    <div className="w-full h-px bg-white-200" />
                    <AirspaceFeaturePanelRawText
                        rawText={rawText}
                        rawIcao={rawIcaoFormat}
                        rawDomestic={rawLocalFormat}
                        featureType={featureType}
                    />
                </>
            )}
        </div>
    )
}

const AirspaceFeaturePanelTitle = ({
    id,
    status: statusProp,
    featureType,
    notamType,
    title,
    location,
}: {
    id: string
    status: AirspaceFeatureStatus
    featureType: AirspaceFeatureType
    notamType?: AirspaceNotamType
    title: string
    location?: string
}) => {
    const status = (() => {
        switch (statusProp) {
            case "active":
                return { type: "error", title: "Active", dotClassName: "text-red-500" } as const
            case "scheduled":
                return {
                    type: "status",
                    title: "Scheduled",
                    dotClassName: "text-yellow-500",
                } as const
            case "permanent":
                return { type: "info", title: "Permanent", dotClassName: "text-blue-500" } as const
        }
    })()
    const type = (() => {
        switch (featureType) {
            case "tfr":
                return "TFR"
            case "notam":
                return notamType ? `NOTAM - ${notamType}` : "NOTAM"
        }
    })()
    const subtitle = featureType === "restriction" ? location : id
    return (
        <div className="flex flex-col gap-3">
            {status && (
                <div className="flex justify-between items-center">
                    <Status type={status.type} variant="outline" className="pl-0.5">
                        <div className="flex items-center">
                            <Icons.ExtraSmallIcon
                                Icon={Icons.Dot}
                                className={status.dotClassName}
                            />
                            <div>{status.title}</div>
                        </div>
                    </Status>
                    {type && <small className="xs text-gray-200">{type}</small>}
                </div>
            )}
            <div className="flex flex-col gap-1.5">
                <div className="text-lg text-white">{title}</div>
                {subtitle && <div className="text-base text-gray-200">{subtitle}</div>}
            </div>
        </div>
    )
}

const AirspaceFeaturePanelTimeRange = ({
    start,
    end,
}: {
    start?: string | null
    end?: string | null
}) => {
    const format = (iso: string) => {
        const d = new Date(iso)
        return {
            date: d.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
            }),
            time: d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
        }
    }
    const s = start ? format(start) : null
    const e = end ? format(end) : null
    return (
        <div className="flex items-center gap-3">
            <div className="flex-1 flex flex-col">
                <div className="text-xs text-gray-300 pb-0.5">Starts</div>
                {s ? (
                    <>
                        <div className="text-white">{s.date}</div>
                        <div className="text-gray-300">{s.time}</div>
                    </>
                ) : (
                    <div className="text-gray-300">—</div>
                )}
            </div>
            <div className="flex-1 flex flex-col">
                <div className="text-xs text-gray-300 pb-0.5">Ends</div>
                {e ? (
                    <>
                        <div className="text-white">{e.date}</div>
                        <div className="text-gray-300">{e.time}</div>
                    </>
                ) : (
                    <div className="text-white">Permanent</div>
                )}
            </div>
        </div>
    )
}

export const formatAltitude = (altitude?: OpenAirAltitude) => {
    if (!altitude) return "—"
    switch (altitude.reference) {
        case "SFC":
            return "SFC"
        case "UNLIMITED":
            return "Unlimited"
        case "FL":
            return `FL${altitude.flightLevel}`
        case "MSL":
        case "AGL":
            return `${altitude.feet.toLocaleString()} ft ${altitude.reference}`
    }
}

const AirspaceFeaturePanelAltitudes = ({
    floor,
    ceiling,
}: {
    floor?: OpenAirAltitude
    ceiling?: OpenAirAltitude
}) => {
    return (
        <div className="flex items-center gap-3">
            <div className="flex-1 flex flex-col gap-0.5">
                <div className="text-xs text-gray-300">Floor</div>
                <div className="text-white font-mono">{formatAltitude(floor)}</div>
            </div>
            <div className="flex-1 flex flex-col gap-0.5">
                <div className="text-xs text-gray-300">Ceiling</div>
                <div className="text-white font-mono">{formatAltitude(ceiling)}</div>
            </div>
        </div>
    )
}

const formatAgencyContact = (name: string, phone?: string) => (phone ? `${name} ${phone}` : name)

const AirspaceFeaturePanelSimpleText = ({ label, value }: { label: string; value: string }) => {
    return (
        <div className="flex flex-col gap-0.5">
            <div className="text-xs text-gray-300">{label}</div>
            <div className="text-white">{value}</div>
        </div>
    )
}

const AirspaceFeaturePanelRawText = ({
    rawText,
    rawIcao,
    rawDomestic,
    featureType,
}: {
    rawText?: string
    rawIcao?: string
    rawDomestic?: string
    featureType: AirspaceFeatureType
}) => {
    const dispatch = useAppDispatch()
    const storedTab = useAppSelector(selectRawTextTab)
    const tab = storedTab ?? (rawIcao ? "icao" : rawDomestic ? "domestic" : "text")
    const isExpanded = useAppSelector(selectRawTextExpanded)
    const type = featureType === "notam" ? "NOTAM" : "TFR"
    return (
        <div
            className="flex flex-col pointer-events-auto [&>*]:pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
        >
            <div
                className="flex justify-between items-center cursor-pointer hover:bg-white-100 py-1 rounded-md"
                onClick={() => dispatch(setRawTextExpanded(!isExpanded))}
            >
                <div className="text-xs text-gray-300">Original Text</div>
                <Icons.SmallIcon
                    Icon={Icons.ArrowDropdown}
                    className={classNames("transition-transform text-gray-300", {
                        "rotate-180": isExpanded,
                    })}
                />
            </div>
            <ExpandableContent isOpen={isExpanded}>
                <div className="flex flex-col gap-2">
                    <div className="flex">
                        <div className="flex-1">
                            <TabHeader
                                currentMode={tab}
                                mode="text"
                                setActiveMode={() => dispatch(setRawTextTab("text"))}
                                title="Text"
                            />
                        </div>
                        <div className="flex-1">
                            <TabHeader
                                currentMode={tab}
                                mode="icao"
                                setActiveMode={() => dispatch(setRawTextTab("icao"))}
                                title="ICAO"
                            />
                        </div>
                        <div className="flex-1">
                            <TabHeader
                                currentMode={tab}
                                mode="domestic"
                                setActiveMode={() => dispatch(setRawTextTab("domestic"))}
                                title="Domestic"
                            />
                        </div>
                    </div>
                    <ScrollableArea className="px-2 max-h-36" onWheel={(e) => e.stopPropagation()}>
                        {tab === "text" && (
                            <small className="break-words">
                                {rawText ?? `Plain text not available for this ${type}`}
                            </small>
                        )}
                        {tab === "icao" && (
                            <small className="break-words">
                                {rawIcao ?? `ICAO Format not available for this ${type}`}
                            </small>
                        )}
                        {tab === "domestic" && (
                            <small className="break-words">
                                {rawDomestic ??
                                    `FAA Domestic Format not available for this ${type}`}
                            </small>
                        )}
                    </ScrollableArea>
                </div>
            </ExpandableContent>
        </div>
    )
}
